import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { DEMO_USERS } from '../data/mockData';
import { apiService } from '../services/apiService';
import { ChevronDown } from 'lucide-react';

const COUNTRIES = [
  { code: '+1', name: 'United States', flag: '🇺🇸' },
  { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', name: 'Australia', flag: '🇦🇺' },
  { code: '+1', name: 'Canada', flag: '🇨🇦' },
  { code: '+91', name: 'India', flag: '🇮🇳' },
  { code: '+65', name: 'Singapore', flag: '🇸🇬' },
  { code: '+852', name: 'Hong Kong', flag: '🇭🇰' },
  { code: '+60', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+971', name: 'UAE', flag: '🇦🇪' },
];

const SECURITY_QUESTIONS = [
  'What is your mother\'s maiden name?',
  'What was the name of your first pet?',
  'What city were you born in?',
  'What is your favorite book?',
  'What was the name of your first school?',
  'What is your favorite movie?',
  'What was your first car\'s make and model?',
  'In what city or town did your mother and father meet?',
];

export default function Auth() {
  const { login, toast } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [mode, setMode] = useState(params.get('mode') || 'login');
  const [role, setRole] = useState(params.get('role') || 'receiver');

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    confirmPassword: '',
    pincode: '',
    password: '',
    securityQuestion: '',
    securityAnswer: '',
  });

  const [forgotPasswordForm, setForgotPasswordForm] = useState({
    email: '',
    step: 'email', // 'email' | 'verify' | 'reset'
    securityQuestion: '',
    securityAnswer: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const [cityLookup, setCityLookup] = useState({
    city: '',
    district: '',
    state: '',
    status: 'idle',
  });

  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[4]);
  const [countryOpen, setCountryOpen] = useState(false);

  useEffect(() => {
    setMode(params.get('mode') || 'login');
    setRole(params.get('role') || 'receiver');
  }, [params]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Pincode lookup
  useEffect(() => {
    const pin = form.pincode.trim();
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
      setCityLookup({ city: '', district: '', state: '', status: 'idle' });
      return;
    }

    setCityLookup(prev => ({ ...prev, status: 'loading' }));

    fetch(`https://api.postalpincode.in/pincode/${pin}`)
      .then(r => r.json())
      .then(data => {
        if (data[0]?.Status === 'Success' && data[0].PostOffice?.length) {
          const po = data[0].PostOffice[0];
          setCityLookup({
            city: po.Division || po.District,
            district: po.District,
            state: po.State,
            status: 'found',
          });
        } else {
          setCityLookup({ city: '', district: '', state: '', status: 'notfound' });
        }
      })
      .catch(() => {
        setCityLookup({ city: '', district: '', state: '', status: 'notfound' });
      });
  }, [form.pincode]);

  // QUICK DEMO LOGIN
  const handleDemoLogin = (demoRole) => {
    const demoUser = DEMO_USERS[demoRole];
    if (demoUser) {
      login(demoUser);
      toast(`Welcome, ${demoUser.name}! 👋`, 'success');
      navigate('/');
    }
  };

  // FORGOT PASSWORD
  const handleForgotPassword = async () => {
    const newErrors = {};
    if (!forgotPasswordForm.email) newErrors.email = 'Email required';
    if (!forgotPasswordForm.newPassword || forgotPasswordForm.newPassword.length < 6) newErrors.newPassword = 'Min 6 characters';
    if (forgotPasswordForm.newPassword !== forgotPasswordForm.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setForgotPasswordLoading(true);
    setErrors({});

    try {
      const response = await apiService.resetPassword(forgotPasswordForm.email, forgotPasswordForm.newPassword);
      if (response.success) {
        toast('Password reset successfully! Please log in with your new password.', 'success');
        setForgotPasswordForm({ email: '', newPassword: '', confirmPassword: '' });
        setMode('login');
      } else {
        setErrors({ submit: response.message || 'Password reset failed' });
        toast(response.message || 'Password reset failed', 'error');
      }
    } catch (error) {
      setErrors({ submit: error.message || 'Password reset failed' });
      toast(error.message || 'Password reset failed', 'error');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // LOGIN
  const handleLogin = async () => {
    const newErrors = {};
    if (!form.email) newErrors.email = 'Email required';
    if (!form.password) newErrors.password = 'Password required';

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await apiService.login(form.email, form.password);

      // Validate that user's role matches the selected role
      const userRole = response.data.role;
      const selectedRole = role === 'receiver' ? 'learner' : role; // Convert 'receiver' to 'learner' for comparison
      const userRoleNormalized = userRole === 'receiver' ? 'learner' : userRole;

      if (userRoleNormalized !== selectedRole) {
        const roleLabel = selectedRole === 'learner' ? 'Learner' : 'Expert';
        const actualRoleLabel = userRoleNormalized === 'learner' ? 'Learner' : 'Expert';
        setErrors({ submit: `This account is registered as ${actualRoleLabel}. Please select ${actualRoleLabel} and try again.` });
        toast(`Account is registered as ${actualRoleLabel}, not ${roleLabel}`, 'error');
        setLoading(false);
        return;
      }

      const user = {
        id: response.data.userId,
        name: `${response.data.firstName} ${response.data.lastName}`,
        role: response.data.role,
        email: response.data.email,
        mobile: response.data.mobile,
        city: response.data.city,
        token: response.data.token,
        bg: response.data.role === 'expert' ? '#0B6E65' : '#D4720C',
      };

      login(user);
      toast('Welcome back! 👋', 'success');

      navigate(response.data.role === 'expert' ? '/expert' : '/learner');
    } catch (error) {
      setErrors({ submit: error.message || 'Login failed' });
      toast(error.message || 'Login failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // REGISTER
  const handleRegister = async () => {
    const errs = {};

    if (!form.firstName) errs.firstName = 'Required';
    if (!form.lastName) errs.lastName = 'Required';
    if (!form.email) errs.email = 'Required';
    if (!form.mobile) errs.mobile = 'Required';

    // if (!form.pincode || form.pincode.length !== 6)
    // else if (cityLookup.status === 'loading')
    //   errs.pincode = 'Looking up pincode…';

    if (!form.password || form.password.length < 6)
      errs.password = 'Min 6 characters';

    if (form.password !== form.confirmPassword)
      errs.confirmPassword = 'Passwords do not match';

    if (!form.securityQuestion)
      errs.securityQuestion = 'Please select a security question';

    if (!form.securityAnswer || form.securityAnswer.trim().length < 2)
      errs.securityAnswer = 'Please provide an answer';

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const signupData = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        confirmPassword: form.confirmPassword,
        mobile: `${selectedCountry.code}${form.mobile}`,
        role: role === 'receiver' ? 'learner' : 'expert',
        pincode: form.pincode,
        city: cityLookup.city,
        securityQuestion: form.securityQuestion,
        securityAnswer: form.securityAnswer.trim().toLowerCase(),
      };

      const response = await apiService.signup(signupData);

      const newUser = {
        id: response.data.userId,
        name: `${response.data.firstName} ${response.data.lastName}`,
        role: response.data.role,
        email: response.data.email,
        token: response.data.token,
        bg: response.data.role === 'expert' ? '#0B6E65' : '#D4720C',
      };

      login(newUser);
      toast(`Account created! Welcome, ${form.firstName} 🎉`, 'success');

      navigate(response.data.role === 'expert' ? '/expert' : '/learner');
    } catch (error) {
      setErrors({ submit: error.message || 'Signup failed' });
      toast(error.message || 'Signup failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 60px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg)' }}>
      <div className="card" style={{ maxWidth: 480, width: '100%', padding: 40, animation: 'fadeUp 0.3s ease' }}>
        {/* Header */}
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--saffron)', marginBottom: 8, textAlign: 'center' }}>✦ MakeMyExpert</div>

        {mode === 'login' ? (
          <>
            {/* LOGIN MODE */}
            <h2 style={{ fontSize: 24, marginBottom: 4, textAlign: 'center' }}>Welcome back</h2>
            <p className="text-sm text-ink3" style={{ textAlign: 'center', marginBottom: 28 }}>Log in to your account</p>

            {/* Role Picker */}
            <div className="grid-2" style={{ marginBottom: 24 }}>
              {[
                { id: 'receiver', icon: '📚', label: 'Learner', desc: 'Book sessions' },
                { id: 'expert', icon: '🎓', label: 'Expert', desc: 'Offer sessions' }
              ].map(r => (
                <div
                  key={r.id}
                  onClick={() => setRole(r.id)}
                  style={{
                    border: `1.5px solid ${role === r.id ? 'var(--saffron)' : 'var(--border)'}`,
                    background: role === r.id ? 'var(--saffron-lt)' : 'var(--surface)',
                    borderRadius: 10,
                    padding: '14px 12px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'var(--transition)'
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 6 }}>{r.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 2 }}>{r.desc}</div>
                </div>
              ))}
            </div>

            {/* Email */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={e => set('email', e.target.value)}
              />
              {errors.email && <div className="form-error">{errors.email}</div>}
            </div>

            {/* Password */}
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={e => set('password', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
              />
              {errors.password && <div className="form-error">{errors.password}</div>}
            </div>
            <div style={{ textAlign: 'right', marginBottom: 24 }}>
              <span style={{ color: 'var(--saffron)', cursor: 'pointer', fontWeight: 600, fontSize: 13 }} onClick={() => { setMode('forgot'); setErrors({}); }}>
                Forgot password?
              </span>
            </div>

            {errors.submit && <div className="form-error" style={{ marginBottom: 16 }}>{errors.submit}</div>}

            <button
              onClick={handleLogin}
              disabled={loading}
              className="btn btn-primary w-full"
              style={{ justifyContent: 'center', marginBottom: 16, padding: '12px 24px', fontSize: 14, fontWeight: 600 }}
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>


            <p className="text-sm text-ink3" style={{ textAlign: 'center' }}>
              No account? <span style={{ color: 'var(--saffron)', cursor: 'pointer', fontWeight: 600 }} onClick={() => setMode('register')}>Sign up</span>
            </p>
          </>
        ) : mode === 'forgot' ? (
          <>
            {/* FORGOT PASSWORD MODE - STEP 1: EMAIL */}
            {forgotPasswordForm.step === 'email' && (
              <>
                <h2 style={{ fontSize: 24, marginBottom: 4, textAlign: 'center' }}>Reset password</h2>
                <p className="text-sm text-ink3" style={{ textAlign: 'center', marginBottom: 24 }}>Enter your email address</p>

                <div className="form-group" style={{ marginBottom: 24 }}>
                  <label className="form-label">Email</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotPasswordForm.email}
                    onChange={e => setForgotPasswordForm(f => ({ ...f, email: e.target.value }))}
                    autoFocus
                  />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>

                {errors.submit && <div className="form-error" style={{ marginBottom: 16 }}>{errors.submit}</div>}

                <button
                  onClick={async () => {
                    if (!forgotPasswordForm.email) {
                      setErrors({ email: 'Email is required' });
                      return;
                    }
                    setForgotPasswordLoading(true);
                    setErrors({});
                    try {
                      const res = await apiService.getSecurityQuestion(forgotPasswordForm.email);
                      if (res.success) {
                        setForgotPasswordForm(f => ({ ...f, step: 'verify', securityQuestion: res.data.securityQuestion }));
                      } else {
                        setErrors({ submit: res.message || 'Email not found' });
                      }
                    } catch (err) {
                      setErrors({ submit: err.message || 'Failed to load security question' });
                    } finally {
                      setForgotPasswordLoading(false);
                    }
                  }}
                  disabled={forgotPasswordLoading}
                  className="btn btn-primary w-full"
                  style={{ justifyContent: 'center', marginBottom: 16, padding: '12px 24px', fontSize: 14, fontWeight: 600 }}
                >
                  {forgotPasswordLoading ? 'Loading...' : 'Continue'}
                </button>

                <p className="text-sm text-ink3" style={{ textAlign: 'center' }}>
                  Remember password? <span style={{ color: 'var(--saffron)', cursor: 'pointer', fontWeight: 600 }} onClick={() => { setMode('login'); setErrors({}); }}>Log in</span>
                </p>
              </>
            )}

            {/* FORGOT PASSWORD MODE - STEP 2: VERIFY SECURITY QUESTION */}
            {forgotPasswordForm.step === 'verify' && (
              <>
                <h2 style={{ fontSize: 24, marginBottom: 4, textAlign: 'center' }}>Verify identity</h2>
                <p className="text-sm text-ink3" style={{ textAlign: 'center', marginBottom: 24 }}>Answer your security question</p>

                <div className="form-group" style={{ marginBottom: 16, background: 'var(--bg2)', padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>{forgotPasswordForm.securityQuestion}</div>
                </div>

                <div className="form-group" style={{ marginBottom: 24 }}>
                  <label className="form-label">Your Answer</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Enter your answer"
                    value={forgotPasswordForm.securityAnswer}
                    onChange={e => setForgotPasswordForm(f => ({ ...f, securityAnswer: e.target.value }))}
                    autoFocus
                  />
                  {errors.securityAnswer && <div className="form-error">{errors.securityAnswer}</div>}
                </div>

                {errors.submit && <div className="form-error" style={{ marginBottom: 16 }}>{errors.submit}</div>}

                <button
                  onClick={async () => {
                    if (!forgotPasswordForm.securityAnswer) {
                      setErrors({ securityAnswer: 'Answer is required' });
                      return;
                    }
                    setForgotPasswordLoading(true);
                    setErrors({});
                    try {
                      const res = await apiService.verifySecurityAnswer(forgotPasswordForm.email, forgotPasswordForm.securityAnswer);
                      if (res.success) {
                        setForgotPasswordForm(f => ({ ...f, step: 'reset' }));
                      } else {
                        setErrors({ submit: 'Incorrect answer. Please try again.' });
                      }
                    } catch (err) {
                      setErrors({ submit: err.message || 'Verification failed' });
                    } finally {
                      setForgotPasswordLoading(false);
                    }
                  }}
                  disabled={forgotPasswordLoading}
                  className="btn btn-primary w-full"
                  style={{ justifyContent: 'center', marginBottom: 16, padding: '12px 24px', fontSize: 14, fontWeight: 600 }}
                >
                  {forgotPasswordLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>

                <button
                  onClick={() => {
                    setForgotPasswordForm(f => ({ ...f, step: 'email', securityAnswer: '' }));
                    setErrors({});
                  }}
                  className="btn btn-secondary w-full"
                  style={{ justifyContent: 'center', padding: '12px 24px', fontSize: 14 }}
                >
                  ← Back
                </button>
              </>
            )}

            {/* FORGOT PASSWORD MODE - STEP 3: RESET PASSWORD */}
            {forgotPasswordForm.step === 'reset' && (
              <>
                <h2 style={{ fontSize: 24, marginBottom: 4, textAlign: 'center' }}>Create new password</h2>
                <p className="text-sm text-ink3" style={{ textAlign: 'center', marginBottom: 24 }}>Enter your new password</p>

                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">New Password</label>
                  <input
                    className="form-input"
                    type="password"
                    placeholder="••••••••"
                    value={forgotPasswordForm.newPassword}
                    onChange={e => setForgotPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                    autoFocus
                  />
                  {errors.newPassword && <div className="form-error">{errors.newPassword}</div>}
                </div>

                <div className="form-group" style={{ marginBottom: 24 }}>
                  <label className="form-label">Confirm Password</label>
                  <input
                    className="form-input"
                    type="password"
                    placeholder="••••••••"
                    value={forgotPasswordForm.confirmPassword}
                    onChange={e => setForgotPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                  />
                  {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
                </div>

                {errors.submit && <div className="form-error" style={{ marginBottom: 16 }}>{errors.submit}</div>}

                <button
                  onClick={async () => {
                    const newErrors = {};
                    if (!forgotPasswordForm.newPassword || forgotPasswordForm.newPassword.length < 6) {
                      newErrors.newPassword = 'Min 6 characters';
                    }
                    if (forgotPasswordForm.newPassword !== forgotPasswordForm.confirmPassword) {
                      newErrors.confirmPassword = 'Passwords do not match';
                    }
                    if (Object.keys(newErrors).length) {
                      setErrors(newErrors);
                      return;
                    }

                    setForgotPasswordLoading(true);
                    setErrors({});
                    try {
                      const res = await apiService.resetPassword(forgotPasswordForm.email, forgotPasswordForm.newPassword);
                      if (res.success) {
                        toast('Password reset successfully! Please log in with your new password.', 'success');
                        setMode('login');
                        setForgotPasswordForm({ email: '', step: 'email', securityQuestion: '', securityAnswer: '', newPassword: '', confirmPassword: '' });
                      } else {
                        setErrors({ submit: res.message || 'Failed to reset password' });
                      }
                    } catch (err) {
                      setErrors({ submit: err.message || 'Failed to reset password' });
                    } finally {
                      setForgotPasswordLoading(false);
                    }
                  }}
                  disabled={forgotPasswordLoading}
                  className="btn btn-primary w-full"
                  style={{ justifyContent: 'center', marginBottom: 16, padding: '12px 24px', fontSize: 14, fontWeight: 600 }}
                >
                  {forgotPasswordLoading ? 'Resetting...' : 'Reset Password'}
                </button>

                <button
                  onClick={() => {
                    setForgotPasswordForm(f => ({ ...f, step: 'verify', securityAnswer: '' }));
                    setErrors({});
                  }}
                  className="btn btn-secondary w-full"
                  style={{ justifyContent: 'center', padding: '12px 24px', fontSize: 14 }}
                >
                  ← Back
                </button>
              </>
            )}
          </>
        ) : (
          <>
            {/* SIGNUP MODE */}
            <h2 style={{ fontSize: 24, marginBottom: 4, textAlign: 'center' }}>Create account</h2>
            <p className="text-sm text-ink3" style={{ textAlign: 'center', marginBottom: 24 }}>Join as a learner or expert</p>

            {/* Role Picker */}
            <div className="grid-2" style={{ marginBottom: 20 }}>
              {[
                { id: 'receiver', icon: '📚', label: 'Learner', desc: 'Book sessions' },
                { id: 'expert', icon: '🎓', label: 'Expert', desc: 'Offer sessions' }
              ].map(r => (
                <div
                  key={r.id}
                  onClick={() => setRole(r.id)}
                  style={{
                    border: `1.5px solid ${role === r.id ? 'var(--saffron)' : 'var(--border)'}`,
                    background: role === r.id ? 'var(--saffron-lt)' : 'var(--surface)',
                    borderRadius: 10,
                    padding: '14px 12px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'var(--transition)'
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 6 }}>{r.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 2 }}>{r.desc}</div>
                </div>
              ))}
            </div>

            {/* First & Last Name */}
            <div className="grid-2" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">First name</label>
                <input
                  className="form-input"
                  placeholder="First name"
                  value={form.firstName}
                  onChange={e => set('firstName', e.target.value)}
                />
                {errors.firstName && <div className="form-error">{errors.firstName}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Last name</label>
                <input
                  className="form-input"
                  placeholder="Last name"
                  value={form.lastName}
                  onChange={e => set('lastName', e.target.value)}
                />
                {errors.lastName && <div className="form-error">{errors.lastName}</div>}
              </div>
            </div>

            {/* Email */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={e => set('email', e.target.value)}
              />
              {errors.email && <div className="form-error">{errors.email}</div>}
            </div>

            {/* Mobile Number */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Mobile Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Country Dropdown */}
                <div style={{ position: 'relative', width: 100 }}>
                  <button
                    onClick={() => setCountryOpen(!countryOpen)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 6,
                      padding: '10px 10px',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      background: 'var(--surface)',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600
                    }}
                  >
                    <span>{selectedCountry.flag} {selectedCountry.code}</span>
                    <ChevronDown size={14} style={{ color: 'var(--ink4)' }} />
                  </button>
                  {countryOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: 4,
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      boxShadow: 'var(--shadow)',
                      zIndex: 100,
                      maxHeight: 280,
                      overflow: 'auto'
                    }}>
                      {COUNTRIES.map((c, i) => (
                        <div
                          key={i}
                          onClick={() => { setSelectedCountry(c); setCountryOpen(false); }}
                          style={{
                            padding: '10px 12px',
                            fontSize: 13,
                            cursor: 'pointer',
                            color: selectedCountry.code === c.code ? 'var(--saffron)' : 'var(--ink2)',
                            background: selectedCountry.code === c.code ? 'var(--saffron-lt)' : 'transparent',
                            transition: 'var(--transition)'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                          onMouseLeave={e => e.currentTarget.style.background = selectedCountry.code === c.code ? 'var(--saffron-lt)' : 'transparent'}
                        >
                          {c.flag} {c.code} {c.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Mobile Input */}
                <input
                  className="form-input"
                  style={{ flex: 1 }}
                  type="tel"
                  placeholder="9876543210"
                  value={form.mobile}
                  onChange={e => set('mobile', e.target.value.replace(/\D/g, ''))}
                  maxLength={15}
                />
              </div>
              {errors.mobile && <div className="form-error">{errors.mobile}</div>}
            </div>

            {/* Pincode */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Pincode</label>
              <input
                className="form-input"
                placeholder="e.g. 400001"
                maxLength={6}
                value={form.pincode}
                onChange={e => { set('pincode', e.target.value.replace(/\D/g, '')); setErrors(err => ({ ...err, pincode: '' })); }}
              />
              {cityLookup.status === 'loading' && (
                <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 4 }}>Looking up pincode…</div>
              )}
              {cityLookup.status === 'found' && (
                <div style={{ fontSize: 12, color: 'var(--teal)', marginTop: 4, fontWeight: 600 }}>
                  📍 {cityLookup.city}, {cityLookup.district}, {cityLookup.state}
                </div>
              )}
              {cityLookup.status === 'notfound' && (
                <div style={{ fontSize: 12, color: '#C13030', marginTop: 4 }}>Pincode not found. Please check and try again.</div>
              )}
              {errors.pincode && <div className="form-error">{errors.pincode}</div>}
            </div>

            {/* Password */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type="password"
                placeholder="Min 6 characters"
                value={form.password}
                onChange={e => set('password', e.target.value)}
              />
              {errors.password && <div className="form-error">{errors.password}</div>}
            </div>

            {/* Confirm Password */}
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Confirm Password</label>
              <input
                className="form-input"
                type="password"
                placeholder="Re-enter password"
                value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)}
              />
              {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
            </div>

            {/* Security Question */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Security Question</label>
              <select
                className="form-select"
                value={form.securityQuestion}
                onChange={e => set('securityQuestion', e.target.value)}
              >
                <option value="">— Choose a security question —</option>
                {SECURITY_QUESTIONS.map((q, i) => (
                  <option key={i} value={q}>{q}</option>
                ))}
              </select>
              {errors.securityQuestion && <div className="form-error">{errors.securityQuestion}</div>}
            </div>

            {/* Security Answer */}
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Your Answer</label>
              <input
                className="form-input"
                type="text"
                placeholder="Your answer (case-insensitive)"
                value={form.securityAnswer}
                onChange={e => set('securityAnswer', e.target.value)}
              />
              <p style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 4 }}>💡 You'll need this to reset your password if you forget it</p>
              {errors.securityAnswer && <div className="form-error">{errors.securityAnswer}</div>}
            </div>

            {errors.submit && <div className="form-error" style={{ marginBottom: 16 }}>{errors.submit}</div>}

            <button
              onClick={handleRegister}
              disabled={loading || cityLookup.status === 'loading'}
              className="btn btn-primary w-full"
              style={{ justifyContent: 'center', marginBottom: 16, padding: '12px 24px', fontSize: 14, fontWeight: 600 }}
            >
              {loading ? 'Signing up...' : 'Join Now'}
            </button>

            <p className="text-sm text-ink3" style={{ textAlign: 'center' }}>
              Have an account? <span style={{ color: 'var(--saffron)', cursor: 'pointer', fontWeight: 600 }} onClick={() => setMode('login')}>Log in</span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}